import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { ScrollContext, ViewOnlyContext } from '../context';
import EventMapHeader from '../components/EventMapHeader';
import { useRef, useEffect } from 'react';
import DefaultLayout from 'src/layouts/DefaultLayout';
import { eventTabsConfig } from 'src/routes';
import { usePermissions } from 'src/hooks/usePermissions';
import { useEventHasMap } from 'src/hooks/useEventHasMap';
import { getDefaultEventTabWhenNoMap } from 'src/lib/eventDefaultTab';
import PermissionDenied from 'src/components/PermissionDenied';
import EventBottomNavigation from 'src/components/EventBottomNavigation';

const VIEW_ONLY_ALLOWED_TABS = ['info', 'map', 'notes', 'todo'];

interface Props {
    viewOnly?: boolean;
}

export default function EventScreen(props: Props) {
    const viewOnly = props.viewOnly || false;
    const routePrefix = viewOnly ? '/viewevent' : '/event';
    const routeTemplate = `/${routePrefix}/:eventId/:activeTab`;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const routeMatch = useMatch(routeTemplate);
    const eventId = routeMatch?.params?.eventId;
    const activeTab = routeMatch?.params?.activeTab;
    const secret = searchParams.get('secret');
    const searchSuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const {
        canAccessEvent,
        isLoading: permissionsLoading,
        canViewWasteData,
        canViewInventory,
        canEditUsers,
        canEditEvents,
    } = usePermissions();
    const { hasMap, isLoading: eventMapsLoading } = useEventHasMap(eventId);
    const scrollRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        const container = scrollRef.current;
        if (!container) return;
        container.scrollTop = container.scrollHeight;
    };

    // View-only with secret: only info, map, notes, and todo tabs are available; redirect others to map (or info if no map)
    useEffect(() => {
        if (!viewOnly || !secret || !eventId || !activeTab) return;
        if (VIEW_ONLY_ALLOWED_TABS.includes(activeTab)) return;
        if (eventMapsLoading) return;
        const fallback = hasMap ? 'map' : 'info';
        navigate(`/viewevent/${eventId}/${fallback}?secret=${encodeURIComponent(secret)}`, { replace: true });
    }, [viewOnly, secret, eventId, activeTab, navigate, eventMapsLoading, hasMap]);

    // Map / to-do require a map; send users to a sensible default tab when the event has none
    useEffect(() => {
        if (!eventId || !activeTab || eventMapsLoading) return;
        if (permissionsLoading) return;
        if (hasMap) return;
        if (activeTab !== 'map' && activeTab !== 'todo') return;
        if (viewOnly && secret) {
            navigate(`/viewevent/${eventId}/info?secret=${encodeURIComponent(secret)}`, { replace: true });
            return;
        }
        if (!viewOnly) {
            const target = getDefaultEventTabWhenNoMap({
                canViewWasteData,
                canViewInventory,
                canEditUsers,
                canEditEvents,
            });
            navigate(`/event/${eventId}/${target}${searchSuffix}`, { replace: true });
        }
    }, [
        eventId,
        activeTab,
        eventMapsLoading,
        permissionsLoading,
        hasMap,
        viewOnly,
        secret,
        navigate,
        searchSuffix,
        canViewWasteData,
        canViewInventory,
        canEditUsers,
        canEditEvents,
    ]);

    if (!routeMatch) {
        return <p>ERROR: unknown route</p>;
    }

    if (!eventId) {
        return <p>ERROR: event id</p>;
    }

    if (!viewOnly && !permissionsLoading && !canAccessEvent(eventId)) {
        return <PermissionDenied message="You don't have access to this event" />;
    }

    // When view-only with secret, only allow info, map, notes, todo (redirect above handles invalid tabs)
    const effectiveTab =
        viewOnly && secret && !VIEW_ONLY_ALLOWED_TABS.includes(activeTab ?? '')
            ? eventMapsLoading
                ? 'map'
                : hasMap
                  ? 'map'
                  : 'info'
            : activeTab;
    const currentTab = eventTabsConfig.find(tab => tab.id === effectiveTab);

    if (!currentTab) {
        return <p>ERROR: Unknown tab {effectiveTab}</p>;
    }

    const TabComponent = currentTab.component;

    return (
        <ViewOnlyContext.Provider value={viewOnly}>
            <DefaultLayout>
                <Box
                    id="event-content"
                    ref={scrollRef}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        overflowY: 'auto',
                        height: '100%',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        alignItems: 'center',
                        overflowX: 'hidden',
                    }}
                >
                    <ScrollContext.Provider value={{ scrollToBottom }}>
                        <EventMapHeader eventId={eventId} />
                        <Box
                            id="event-inner-content"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                width: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                                alignItems: 'center',
                                minHeight: 0,
                            }}
                        >
                            <TabComponent eventId={eventId} />
                        </Box>
                    </ScrollContext.Provider>
                </Box>
                <EventBottomNavigation eventId={eventId} routePrefix={routePrefix} />
            </DefaultLayout>
        </ViewOnlyContext.Provider>
    );
}
